import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from '../login/LoginForm';
import SearchableSelect from '../../components/ui/SearchableSelect';
import Notification from '../../components/ui/Notification';
import Modal from '../../components/ui/Modal';
import { transcriptApi } from '../../api/transcriptApi';
import { wpApi } from '../../api/wpApi';
import { Link } from 'react-router-dom';
import { TRANSCRIPT_PROVIDERS, DEFAULT_PROVIDER } from '../../constants/transcriptProviders';
import TranscriptStatusDisplay from '../../components/reusable/TranscriptStatusDisplay';
import SessionQueueStatus from '../../components/reusable/SessionQueueStatus';
import { formatDuration } from '../../utils/formatDuration';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  Squares2X2Icon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  TrashIcon,
  BookmarkIcon,
  StarIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';

export default function SessionsPage() {
  const { user, loading, error, getAuth } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [notif, setNotif] = useState(null);

  // Retry transcript modal
  const [retryModalOpen, setRetryModalOpen] = useState(false);
  const [selectedSessionForRetry, setSelectedSessionForRetry] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [retryingSession, setRetryingSession] = useState(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);

  const hasProcessingTranscripts = useMemo(() => (
    sessions.some((session) => {
      const transcriptStatus = session?.transcript_status || session?.transcriptStatus;
      const sessionStatus = session?.status;
      return (
        ['PROCESSING', 'PENDING', 'IN_PROGRESS'].includes(transcriptStatus) ||
        ['PROCESSING', 'IN_PROGRESS'].includes(sessionStatus)
      );
    })
  ), [sessions]);

  // Filter presets
  const [filterPresets, setFilterPresets] = useState(() => {
    try {
      const saved = localStorage.getItem('sessionFilterPresets');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [savePresetModalOpen, setSavePresetModalOpen] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Bulk actions
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  
  // Expanded transcript sections
  const [expandedTranscriptSections, setExpandedTranscriptSections] = useState(new Set());
  
  const toggleTranscriptSection = (sessionId) => {
    setExpandedTranscriptSections(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) {
        next.delete(sessionId);
      } else {
        next.add(sessionId);
      }
      return next;
    });
  };

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = localStorage.getItem('sessionRecentSearches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('sessionFavorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [status, setStatus] = useState('all');
  const [model, setModel] = useState('all');
  const [sort, setSort] = useState('date_desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [view, setView] = useState('cards'); // 'cards' | 'table'

  const statusOptions = useMemo(() => ([
    { value: 'all', label: 'All' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'IN_PROGRESS', label: 'In progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'FAILED', label: 'Failed' },
  ]), []);

  const modelOptions = useMemo(() => ([
    { value: 'all', label: 'All models' },
    { value: 'whisper-small', label: 'Whisper Small' },
    { value: 'whisper-medium', label: 'Whisper Medium' },
    { value: 'whisper-large-v3', label: 'Whisper Large v3' },
    { value: 'distil-whisper-large-v3', label: 'Distil-Whisper Large v3' },
    { value: 'openai-whisper-1', label: 'OpenAI Whisper-1 (deprecated)' },
  ]), []);

  useEffect(() => {
    if (!user) return;
    const loadEvents = async () => {
      try {
        const data = await wpApi.getEventsWithChildren();
        setEvents((data || []).map(ev => ({ id: ev.ID, title: ev.post_title })));
      } catch (e) {
        // Non-blocking
      }
    };
    loadEvents();
  }, [user]);

  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(search);
      // Save to recent searches if not empty
      if (search.trim() && search.trim().length > 2) {
        setRecentSearches(prev => {
          const updated = [search.trim(), ...prev.filter(s => s !== search.trim())].slice(0, 10);
          localStorage.setItem('sessionRecentSearches', JSON.stringify(updated));
          return updated;
        });
      }
    }, 500);

    return () => clearTimeout(timerId);
  }, [search]);

  const applyRecentSearch = (searchTerm) => {
    setSearch(searchTerm);
    setShowRecentSearches(false);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('sessionRecentSearches');
  };

  const toggleFavorite = (sessionId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId];
      localStorage.setItem('sessionFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const isFavorite = (sessionId) => favorites.includes(sessionId);

  const loadSessions = useCallback(async () => {
    try {
      setLoadingList(true);
      const params = {
        search: debouncedSearch || undefined,
        status: status !== 'all' ? status : undefined,
        model: model !== 'all' ? model : undefined,
        ordering: sort,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        events: selectedEvents.length > 0 ? selectedEvents.map(e => e.id).join(',') : undefined,
        user_id: selectedUser?.id || undefined,
        page,
        per_page: perPage,
      };
      const data = await transcriptApi.getSessions(params);
      // Backend returns { sessions: [...], total, skip, limit }
      let list = Array.isArray(data?.sessions) ? data.sessions : (Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []));
      
      // Filter by favorites if enabled
      if (showFavoritesOnly) {
        list = list.filter(s => favorites.includes(s.id || s.session_id));
      }
      
      // Map camelCase to snake_case for compatibility
      list = list.map(session => ({
        id: session.id,
        session_id: session.id,
        user_id: session.userId || session.user_id,
        user_name: session.userName || session.user_name,
        user_email: session.userEmail || session.user_email,
        title: session.title,
        description: session.description,
        original_file_id: session.originalFileId || session.original_file_id,
        original_filename: session.originalFilename || session.original_filename,
        status: session.status,
        storage_path: session.storagePath || session.storage_path,
        file_path: session.filePath || session.file_path,
        file_url: session.fileUrl || session.file_url,
        video_url: session.videoUrl || session.video_url,
        url: session.url,
        processing_started_at: session.processingStartedAt || session.processing_started_at,
        processing_completed_at: session.processingCompletedAt || session.processing_completed_at,
        error_message: session.errorMessage || session.error_message,
        event_id: session.eventId || session.event_id,
        event_title: session.eventTitle || session.event_title,
        created_at: session.createdAt || session.created_at,
        updated_at: session.updatedAt || session.updated_at,
        transcript_id: session.transcriptId || session.transcript_id,
        report_id: session.reportId || session.report_id,
        report_url: session.reportUrl || session.report_url,
        duration_seconds: session.durationSeconds || session.duration_seconds,
        has_transcripts: session.hasTranscripts || session.has_transcripts || false,
        has_reports: session.hasReports || session.has_reports || false,
        transcript_count: session.transcriptCount || session.transcript_count || 0,
        report_count: session.reportCount || session.report_count || 0,
        // Keep original camelCase fields too for backward compatibility
        ...session
      }));
      
      setSessions(list);
      setTotalCount(showFavoritesOnly ? list.length : (data?.total || data?.count || list.length));
      
      // Extract unique users from sessions for user filter dropdown
      const uniqueUsers = {};
      list.forEach(session => {
        const userId = session.user_id || session.userId;
        const userName = session.user_name || session.userName;
        const userEmail = session.user_email || session.userEmail;
        if (userId && !uniqueUsers[userId]) {
          uniqueUsers[userId] = {
            id: userId,
            name: userName || userEmail || 'Unknown User',
            email: userEmail || ''
          };
        }
      });
      setUsers(Object.values(uniqueUsers).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      setNotif({ type: 'error', title: 'Failed to load sessions', message: e.message });
    } finally {
      setLoadingList(false);
    }
  }, [debouncedSearch, status, model, sort, dateFrom, dateTo, selectedEvents, selectedUser, page, perPage, showFavoritesOnly, favorites]);

  // Load sessions when user logs in or when filters change
  useEffect(() => {
    if (!user) return;
    loadSessions();
  }, [user, loadSessions]);

  // Auto-enable refresh when any transcript is generating
  useEffect(() => {
    if (hasProcessingTranscripts && !autoRefresh) {
      setAutoRefresh(true);
    }
  }, [hasProcessingTranscripts, autoRefresh]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh || !user) return;

    console.log('🔄 Auto-refresh enabled - refreshing sessions every 15 seconds');
    
    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refresh: Reloading sessions list...');
      loadSessions();
    }, 15000); // 15 seconds

    return () => {
      console.log('🛑 Auto-refresh disabled');
      clearInterval(intervalId);
    };
  }, [autoRefresh, user, loadSessions]);

  if (!user) {
    return (
      <LoginForm 
        onSSOLogin={getAuth}
        ssoLoading={loading}
        ssoError={error}
      />
    );
  }

  const resetFilters = () => {
    setSearch('');
    setSelectedEvents([]);
    setSelectedUser(null);
    setStatus('all');
    setModel('all');
    setDateFrom('');
    setDateTo('');
    setSort('date_desc');
    setPage(1);
  };

  const getCurrentFilters = () => ({
    search: debouncedSearch,
    selectedEvents: selectedEvents.map(e => e.id),
    selectedUserId: selectedUser?.id,
    status,
    model,
    dateFrom,
    dateTo,
    sort,
  });

  const saveCurrentFilters = () => {
    if (!presetName.trim()) {
      setNotif({ type: 'error', title: 'Error', message: 'Please enter a preset name' });
      return;
    }

    const currentFilters = getCurrentFilters();
    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      filters: currentFilters,
      createdAt: new Date().toISOString(),
    };

    const updatedPresets = [...filterPresets, newPreset];
    setFilterPresets(updatedPresets);
    localStorage.setItem('sessionFilterPresets', JSON.stringify(updatedPresets));
    setPresetName('');
    setSavePresetModalOpen(false);
    setNotif({ type: 'success', title: 'Success', message: 'Filter preset saved' });
  };

  const applyPreset = (preset) => {
    const { filters } = preset;
    setSearch(filters.search || '');
    setSelectedEvents(
      filters.selectedEvents 
        ? events.filter(e => filters.selectedEvents.includes(e.id))
        : []
    );
    setSelectedUser(
      filters.selectedUserId
        ? users.find(u => u.id === filters.selectedUserId) || null
        : null
    );
    setStatus(filters.status || 'all');
    setModel(filters.model || 'all');
    setDateFrom(filters.dateFrom || '');
    setDateTo(filters.dateTo || '');
    setSort(filters.sort || 'date_desc');
    setPage(1);
    setNotif({ type: 'success', title: 'Success', message: `Applied preset: ${preset.name}` });
  };

  const deletePreset = (presetId) => {
    const updatedPresets = filterPresets.filter(p => p.id !== presetId);
    setFilterPresets(updatedPresets);
    localStorage.setItem('sessionFilterPresets', JSON.stringify(updatedPresets));
    setNotif({ type: 'success', title: 'Success', message: 'Preset deleted' });
  };

  // Bulk actions
  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map(s => s.id || s.session_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedSessions.size} session(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      const deletePromises = Array.from(selectedSessions).map(id => 
        transcriptApi.deleteSession(id).catch(err => {
          console.error(`Failed to delete session ${id}:`, err);
          return { error: true, id };
        })
      );
      
      await Promise.all(deletePromises);
      setNotif({ 
        type: 'success', 
        title: 'Success', 
        message: `Deleted ${selectedSessions.size} session(s)` 
      });
      setSelectedSessions(new Set());
      loadSessions();
    } catch (error) {
      setNotif({ type: 'error', title: 'Error', message: 'Some sessions could not be deleted' });
    }
  };

  const handleBulkExport = () => {
    if (selectedSessions.size === 0) return;
    
    const selectedSessionsData = sessions.filter(s => 
      selectedSessions.has(s.id || s.session_id)
    );
    
    const rows = selectedSessionsData.map(s => ({
      id: s.id,
      title: s.title,
      description: s.description,
      status: s.status,
      created_at: s.created_at,
      updated_at: s.updated_at,
    }));
    
    const header = Object.keys(rows[0] || {}).join(',');
    const csv = [header, ...rows.map(r => Object.values(r).map(v => {
      if (v == null) return '';
      const str = String(v).replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${str}"` : str;
    }).join(','))].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessions_bulk_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    setNotif({ type: 'success', title: 'Success', message: `Exported ${selectedSessions.size} session(s)` });
  };

  const exportCsv = () => {
    const rows = sessions.map(s => ({
      id: s.id,
      title: s.title,
      description: s.description,
      url: s.url,
      file_path: s.file_path,
      file_url: s.file_url,
      original_file_id: s.original_file_id,
      original_filename: s.original_filename,
      storage_path: s.storage_path,
      processing_started_at: s.processing_started_at,
      processing_completed_at: s.processing_completed_at,
      error_message: s.error_message,
      duration_seconds: s.duration_seconds,
      status: s.status,
      event_id: s.event_id,
      user_id: s.user_id,
      transcript_id: s.transcript_id,
      report_id: s.report_id,
      created_at: s.created_at,
      updated_at: s.updated_at,
    }));
    const header = Object.keys(rows[0] || { id: '', title: '' }).join(',');
    const csv = [header, ...rows.map(r => Object.values(r).map(v => {
      if (v == null) return '';
      const str = String(v).replace(/"/g, '""');
      return /[",\n]/.test(str) ? `"${str}"` : str;
    }).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sessions.csv';
    a.click();
    URL.revokeObjectURL(url);
    setNotif({ type: 'success', title: 'Success', message: 'Sessions exported as CSV' });
  };

  const exportJson = () => {
    const data = sessions.map(s => ({
      id: s.id,
      title: s.title,
      description: s.description,
      url: s.url,
      file_path: s.file_path,
      file_url: s.file_url,
      original_file_id: s.original_file_id,
      original_filename: s.original_filename,
      storage_path: s.storage_path,
      processing_started_at: s.processing_started_at,
      processing_completed_at: s.processing_completed_at,
      error_message: s.error_message,
      duration_seconds: s.duration_seconds,
      status: s.status,
      event_id: s.event_id,
      user_id: s.user_id,
      transcript_id: s.transcript_id,
      report_id: s.report_id,
      created_at: s.created_at,
      updated_at: s.updated_at,
    }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sessions.json';
    a.click();
    URL.revokeObjectURL(url);
    setNotif({ type: 'success', title: 'Success', message: 'Sessions exported as JSON' });
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotif({ type: 'success', title: 'Copied', message: 'Copied to clipboard' });
    } catch (e) {
      setNotif({ type: 'error', title: 'Copy failed', message: e.message });
    }
  };

  const handleDeleteSession = async (sessionId, sessionTitle) => {
    if (!window.confirm(`Are you sure you want to delete session "${sessionTitle || 'Untitled'}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await transcriptApi.deleteSession(sessionId);
      setNotif({ type: 'success', title: 'Success', message: 'Session deleted successfully' });
      // Reload sessions list
      loadSessions();
    } catch (e) {
      setNotif({ type: 'error', title: 'Delete failed', message: e.message });
    }
  };

  const openRetryModal = (session) => {
    setSelectedSessionForRetry(session);
    setSelectedProvider(DEFAULT_PROVIDER);
    setRetryModalOpen(true);
  };

  const handleRetryTranscript = async () => {
    if (!selectedSessionForRetry) return;

    try {
      setRetryingSession(true);
      await transcriptApi.startTranscriptGeneration(
        selectedSessionForRetry.id,
        selectedProvider
      );
      setNotif({
        type: 'success',
        title: 'Transcript generation started',
        message: `Retrying with ${TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.label || selectedProvider}`
      });
      setRetryModalOpen(false);
      setSelectedSessionForRetry(null);
      // Refresh sessions list
      setTimeout(() => {
        loadSessions();
      }, 1000);
    } catch (err) {
      setNotif({
        type: 'error',
        title: 'Failed to retry',
        message: err.message || 'An error occurred while starting transcript generation'
      });
    } finally {
      setRetryingSession(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sessions</h1>
              <p className="text-gray-600">Search and filter sessions. Filters are applied automatically.</p>
            </div>
            <Link
              to="/upload"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Add Session
            </Link>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={() => setView('cards')} className={`inline-flex items-center px-3 py-1.5 rounded-md border ${view === 'cards' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <Squares2X2Icon className="h-4 w-4 mr-1" /> Cards
            </button>
            <button onClick={() => setView('table')} className={`inline-flex items-center px-3 py-1.5 rounded-md border ${view === 'table' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <TableCellsIcon className="h-4 w-4 mr-1" /> Table
            </button>
            <div className="ml-auto flex items-center gap-2">
              {/* Filter Presets Dropdown */}
              {filterPresets.length > 0 && (
                <div className="relative">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        const preset = filterPresets.find(p => p.id === e.target.value);
                        if (preset) applyPreset(preset);
                        e.target.value = '';
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm"
                    defaultValue=""
                  >
                    <option value="" disabled>Load Preset...</option>
                    {filterPresets.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button 
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`inline-flex items-center px-3 py-1.5 rounded-md border ${showFavoritesOnly ? 'bg-yellow-50 border-yellow-500 text-yellow-700' : 'border-gray-300 hover:bg-gray-50'}`}
                title="Show only favorites"
              >
                <StarIcon className={`h-4 w-4 mr-1 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                Favorites
              </button>
              <button 
                onClick={() => setSavePresetModalOpen(true)}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50"
                title="Save current filters as preset"
              >
                <BookmarkIcon className="h-4 w-4 mr-1" /> Save Preset
              </button>
              <div className="relative group">
                <button className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50">
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Export
                </button>
                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  <button
                    onClick={exportCsv}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700"
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={exportJson}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700"
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
              <button onClick={resetFilters} className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50">
                Reset Filters
              </button>
              <button 
                onClick={() => setAutoRefresh(!autoRefresh)} 
                className={`inline-flex items-center px-3 py-1.5 rounded-md border ${autoRefresh ? 'bg-green-50 border-green-500 text-green-700' : 'border-gray-300 hover:bg-gray-50'}`}
                title={autoRefresh ? 'Auto-refresh is ON (every 15s)' : 'Auto-refresh is OFF'}
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(debouncedSearch || selectedEvents.length > 0 || selectedUser || status !== 'all' || model !== 'all' || dateFrom || dateTo || showFavoritesOnly) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Active filters:</span>
            {debouncedSearch && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Search: "{debouncedSearch}"
                <button onClick={() => setSearch('')} className="hover:text-blue-900">×</button>
              </span>
            )}
            {selectedEvents.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Events: {selectedEvents.length}
                <button onClick={() => setSelectedEvents([])} className="hover:text-blue-900">×</button>
              </span>
            )}
            {selectedUser && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                User: {selectedUser.name}
                <button onClick={() => setSelectedUser(null)} className="hover:text-blue-900">×</button>
              </span>
            )}
            {status !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Status: {status}
                <button onClick={() => setStatus('all')} className="hover:text-blue-900">×</button>
              </span>
            )}
            {model !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Model: {model}
                <button onClick={() => setModel('all')} className="hover:text-blue-900">×</button>
              </span>
            )}
            {(dateFrom || dateTo) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Date: {dateFrom || '...'} to {dateTo || '...'}
                <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="hover:text-blue-900">×</button>
              </span>
            )}
            {showFavoritesOnly && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                Favorites only
                <button onClick={() => setShowFavoritesOnly(false)} className="hover:text-yellow-900">×</button>
              </span>
            )}
            <button
              onClick={resetFilters}
              className="text-xs text-gray-600 hover:text-gray-900 underline"
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Title, description, user, event..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setShowRecentSearches(recentSearches.length > 0)}
                  onBlur={() => setTimeout(() => setShowRecentSearches(false), 200)}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                {showRecentSearches && recentSearches.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-500">Recent Searches</span>
                      <button
                        onClick={clearRecentSearches}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    </div>
                    {recentSearches.map((term, index) => (
                      <button
                        key={index}
                        onClick={() => applyRecentSearch(term)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                        {term}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
              <SearchableSelect
                options={events}
                value={selectedEvents}
                onChange={setSelectedEvents}
                placeholder="Filter by events..."
                optionLabel="title"
                optionValue="id"
                multiple
              />
            </div>

            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">User</label>
              <div className="flex gap-2">
                <SearchableSelect
                  options={users}
                  value={selectedUser ? [selectedUser] : []}
                  onChange={(selected) => {
                    setSelectedUser(selected.length > 0 ? selected[0] : null);
                    setPage(1);
                  }}
                  placeholder="Filter by user..."
                  optionLabel="name"
                  optionValue="id"
                  multiple={false}
                />
                {selectedUser && (
                  <button
                    onClick={() => {
                      setSelectedUser(null);
                      setPage(1);
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm"
                    title="Clear user filter"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
              <select value={model} onChange={e => setModel(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                {modelOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort</label>
              <select value={sort} onChange={e => setSort(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="date_desc">Newest</option>
                <option value="date_asc">Oldest</option>
                <option value="title_asc">Title A-Z</option>
                <option value="title_desc">Title Z-A</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedSessions.size > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-blue-900">
                {selectedSessions.size} session(s) selected
              </span>
              <button
                onClick={toggleSelectAll}
                className="text-sm text-blue-700 hover:text-blue-900 underline"
              >
                {selectedSessions.size === sessions.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkExport}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 text-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Export Selected
              </button>
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 text-sm"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedSessions(new Set())}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List / Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        {view === 'table' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-2 text-left w-12">
                    <input
                      type="checkbox"
                      checked={selectedSessions.size === sessions.length && sessions.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Title</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Event</th>
                  <th className="px-4 py-2 text-left">File</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingList && (
                  <tr><td colSpan="7" className="px-4 py-6 text-center">Loading sessions...</td></tr>
                )}
                {!loadingList && sessions.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-6 text-center text-gray-600">No sessions found.</td></tr>
                )}
                {!loadingList && sessions.map((s) => {
                  const statusClass = s.status === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-700' : s.status === 'FAILED' ? 'bg-red-50 border-red-200 text-red-700' : s.status === 'IN_PROGRESS' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-50 border-gray-200 text-gray-700';
                  const fileLabel = s.original_filename || s.file_path || s.file_url;
                  const sessionId = s.id || s.session_id;
                  const isSelected = selectedSessions.has(sessionId);
                  return (
                    <tr key={s.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSessionSelection(sessionId)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2 max-w-[300px] truncate">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            toggleFavorite(s.id || s.session_id);
                          }}
                          className="text-yellow-400 hover:text-yellow-600 transition-colors"
                          title={isFavorite(s.id || s.session_id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <StarIcon className={`h-4 w-4 ${isFavorite(s.id || s.session_id) ? 'fill-current' : ''}`} />
                        </button>
                        <Link to={`/sessions/${s.id}`} className="text-gray-900 font-medium hover:underline">{s.title || 'Untitled'}</Link>
                      </div>
                      {s.description && <div className="text-gray-500 text-xs truncate">{s.description}</div>}
                    </td>
                      <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full border text-xs ${statusClass}`}>{s.status || 'N/A'}</span></td>
                      <td className="px-4 py-2">{s.event_id ? <Link to={`/events/${s.event_id}`} className="text-blue-600 hover:underline">{s.event_title || s.eventTitle || 'Untitled Event'}</Link> : '—'}</td>
                      <td className="px-4 py-2">
                        {s.file_url ? (
                          <div className="flex items-center gap-2">
                            <a href={s.file_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate max-w-[240px]">{fileLabel || 'Open file'}</a>
                            {s.file_url && <button title="Copy URL" onClick={() => copyToClipboard(s.file_url)} className="text-gray-400 hover:text-gray-600"><ClipboardDocumentIcon className="h-4 w-4" /></button>}
                          </div>
                        ) : (
                          <span className="truncate max-w-[240px] inline-block">{fileLabel || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">{formatDuration(s.duration_seconds)}</td>
                      <td className="px-4 py-2">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {s.report_url && <a href={s.report_url} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs">Report</a>}
                          <Link to={`/sessions/${s.id}/edit`} className="px-2 py-1 rounded bg-primary-600 text-white hover:bg-primary-700 text-xs">Transcript</Link>
                          <Link to={`/sessions/${s.id}`} className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-xs">Details</Link>
                          <button 
                            onClick={() => handleDeleteSession(s.id, s.title)} 
                            className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50 text-xs inline-flex items-center gap-1"
                            title="Delete session"
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
          {loadingList && (
            <div className="bg-white border rounded-lg p-6 text-center">Loading sessions...</div>
          )}

          {!loadingList && sessions.length === 0 && (
            <div className="bg-white border rounded-lg p-8 text-center text-gray-600">No sessions found.</div>
          )}

          {!loadingList && sessions.map((s) => {
            const safe = (key) => s?.[key];
            const duration = safe('duration_seconds');
            const started = safe('processing_started_at');
            const finished = safe('processing_completed_at');
            const fileLabel = safe('original_filename') || safe('file_path') || safe('file_url');
            const sessionId = s.id || s.session_id;
            
            // Try multiple sources for video URL
            let fileUrl = safe('video_url') || safe('videoUrl') || safe('file_url') || safe('fileUrl');
            
            // If we have original_file_id but no URL, construct it
            if (!fileUrl && safe('original_file_id')) {
              fileUrl = `/api/files/${safe('original_file_id')}/content`;
            }
            
            // Fallback: use session video endpoint if we have session ID
            if (!fileUrl && sessionId) {
              fileUrl = `/api/reports/sessions/${sessionId}/video`;
            }
            
            // Construct full URL if relative
            const fullFileUrl = fileUrl && !fileUrl.startsWith('http') 
              ? `${process.env.REACT_APP_API_BASE_URL}${fileUrl}` 
              : fileUrl;
            const created = safe('created_at');
            const updated = safe('updated_at');
            const statusClass = s.status === 'COMPLETED' ? 'bg-green-50 border-green-200 text-green-700' : s.status === 'FAILED' ? 'bg-red-50 border-red-200 text-red-700' : s.status === 'IN_PROGRESS' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : 'bg-gray-50 border-gray-200 text-gray-700';
            const isSelected = selectedSessions.has(sessionId);
            return (
              <div key={s.id || s.session_id} className={`group bg-white rounded-xl shadow-sm hover:shadow-md border transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50/30 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="p-6">
                  <div className="flex items-start gap-6">
                    {/* Left side - Main content */}
                    <div className="flex-1 min-w-0">
                      {/* Header row: checkbox, star, title, status, duration, event, user */}
                      <div className="flex items-start gap-3 mb-5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSessionSelection(sessionId)}
                          className="mt-1.5 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        />
                        <button
                          onClick={() => toggleFavorite(sessionId)}
                          className="text-yellow-400 hover:text-yellow-500 transition-all shrink-0 mt-1 p-0.5 rounded hover:bg-yellow-50"
                          title={isFavorite(sessionId) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <StarIcon className={`h-5 w-5 ${isFavorite(sessionId) ? 'fill-current' : ''}`} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5 flex-wrap mb-2.5">
                            <Link to={`/sessions/${s.id || s.session_id}`} className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors truncate">
                              {s.title || s.name || 'Untitled session'}
                            </Link>
                            <span className={`px-2.5 py-1 rounded-md text-xs font-semibold shrink-0 ${statusClass}`}>{s.status || 'N/A'}</span>
                            {duration != null && (
                              <span className="text-xs px-2.5 py-1 rounded-md bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/60 text-blue-700 font-semibold flex items-center gap-1.5 shrink-0 shadow-sm">
                                <ClockIcon className="h-3.5 w-3.5" />
                                {formatDuration(duration)}
                              </span>
                            )}
                            {s.event_id && (
                              <Link to={`/events/${s.event_id}`} className="text-xs px-2.5 py-1 rounded-md bg-blue-50/80 border border-blue-200/60 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all shrink-0 font-medium">{s.event_title || s.eventTitle || 'Event'}</Link>
                            )}
                            {(s.user_id || s.userId) && (
                              <button
                                onClick={() => {
                                  setSelectedUser({
                                    id: s.user_id || s.userId,
                                    name: s.user_name || s.userName || s.user_email || s.userEmail || 'User',
                                    email: s.user_email || s.userEmail || ''
                                  });
                                  setPage(1);
                                }}
                                className="text-xs px-2.5 py-1 rounded-md bg-gray-50/80 border border-gray-200/60 text-gray-700 hover:bg-gray-100 hover:border-gray-300 transition-all cursor-pointer shrink-0 font-medium"
                                title={`Filter by user: ${s.user_email || s.userEmail || ''}`}
                              >
                                {s.user_name || s.userName || s.user_email || s.userEmail || 'User'}
                              </button>
                            )}
                          </div>
                          
                          {/* Retry/Generate buttons */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {(s.transcript_status === 'FAILED' || s.transcriptStatus === 'FAILED') && (
                              <button
                                onClick={() => openRetryModal(s)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 hover:text-red-900 hover:bg-red-50 rounded-md transition-all border border-red-200/60"
                                title="Retry transcript generation"
                              >
                                <ArrowPathIcon className="h-3.5 w-3.5" />
                                Retry
                              </button>
                            )}
                            {!(s.transcript_status || s.transcriptStatus) && (
                              <button
                                onClick={() => openRetryModal(s)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 rounded-md transition-all border border-blue-200/60"
                                title="Generate transcript"
                              >
                                <ArrowPathIcon className="h-3.5 w-3.5" />
                                Generate
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Error message */}
                      {s.error_message && s.status === 'FAILED' && (
                        <div className="mb-5 bg-red-50/80 border-l-4 border-red-400 rounded-r-lg p-3.5">
                          <p className="text-sm text-red-800 font-medium">
                            <strong>Error:</strong> {s.error_message}
                          </p>
                        </div>
                      )}

                      {/* File and Processing Information */}
                      <div className="bg-gray-50/50 rounded-lg p-4 border border-gray-100 mb-5">
                        <div className="space-y-3">
                          {/* File Link */}
                          <div>
                            {fullFileUrl ? (
                              <a 
                                href={fullFileUrl} 
                                target="_blank" 
                                rel="noreferrer" 
                                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium cursor-pointer"
                                title={`Open ${fileLabel || 'file'} in new tab`}
                              >
                                <DocumentTextIcon className="h-4 w-4 shrink-0" />
                                <span className="break-all underline">{fileLabel || 'Open file'}</span>
                              </a>
                            ) : fileLabel ? (
                              <span className="text-sm text-gray-500">{fileLabel}</span>
                            ) : null}
                          </div>
                          
                          {/* Processing Details */}
                          <div className="space-y-2 text-xs">
                            {duration != null && (
                              <div className="flex items-center gap-1.5 pb-2 border-b border-gray-200">
                                <ClockIcon className="h-3.5 w-3.5 text-blue-600" />
                                <span className="font-semibold text-gray-900">{formatDuration(duration)}</span>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                              <div className="text-gray-600">
                                <span className="font-medium">Started:</span>
                                <div className="text-gray-900 mt-0.5">{started ? new Date(started).toLocaleString() : '—'}</div>
                              </div>
                              <div className="text-gray-600">
                                <span className="font-medium">Completed:</span>
                                <div className="text-gray-900 mt-0.5">{finished ? new Date(finished).toLocaleString() : '—'}</div>
                              </div>
                              <div className="text-gray-600">
                                <span className="font-medium">Created:</span>
                                <div className="text-gray-900 mt-0.5">{created ? new Date(created).toLocaleString() : '—'}</div>
                              </div>
                              <div className="text-gray-600">
                                <span className="font-medium">Updated:</span>
                                <div className="text-gray-900 mt-0.5">{updated ? new Date(updated).toLocaleString() : '—'}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Transcript Status - Expandable (Bottom left) */}
                      {(s.transcript_status || s.transcriptStatus) && (
                        <div className="bg-gradient-to-r from-gray-50 to-gray-50/80 rounded-lg border border-gray-200/80 overflow-hidden shadow-sm">
                          <button
                            onClick={() => toggleTranscriptSection(sessionId)}
                            className="w-full p-3 flex items-center justify-between hover:bg-gray-100/80 transition-all text-left group"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide shrink-0">Transcript</div>
                              <div className="flex items-center flex-1 min-w-0">
                                <TranscriptStatusDisplay 
                                  status={s.transcript_status || s.transcriptStatus}
                                  startedAt={s.transcript_processing_started_at || s.transcriptProcessingStartedAt}
                                  completedAt={s.transcript_processing_completed_at || s.transcriptProcessingCompletedAt}
                                  duration={s.transcript_processing_duration || s.transcriptProcessingDuration}
                                  errorMessage={s.transcript_error_message || s.transcriptErrorMessage}
                                  provider={s.transcript_provider || s.transcriptProvider}
                                  compact={true}
                                />
                              </div>
                            </div>
                            {expandedTranscriptSections.has(sessionId) ? (
                              <ChevronUpIcon className="h-4 w-4 text-gray-500 shrink-0 ml-2 group-hover:text-gray-700 transition-colors" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4 text-gray-500 shrink-0 ml-2 group-hover:text-gray-700 transition-colors" />
                            )}
                          </button>
                          {expandedTranscriptSections.has(sessionId) && (
                            <div className="px-3 pb-3 pt-2 border-t border-gray-200/80 bg-white/50">
                              <div className="text-xs text-gray-700 space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Provider:</span>
                                  <span className="font-medium text-gray-900">{s.transcript_provider || s.transcriptProvider || '—'}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Started:</span>
                                  <span className="font-medium text-gray-900 text-xs">
                                    {(s.transcript_processing_started_at || s.transcriptProcessingStartedAt) 
                                      ? new Date(s.transcript_processing_started_at || s.transcriptProcessingStartedAt).toLocaleString() 
                                      : '—'}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Completed:</span>
                                  <span className="font-medium text-gray-900 text-xs">
                                    {(s.transcript_processing_completed_at || s.transcriptProcessingCompletedAt) 
                                      ? new Date(s.transcript_processing_completed_at || s.transcriptProcessingCompletedAt).toLocaleString() 
                                      : '—'}
                                  </span>
                                </div>
                                {(s.transcript_processing_duration || s.transcriptProcessingDuration) && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Duration:</span>
                                    <span className="font-medium text-gray-900 text-xs">
                                      {formatDuration(s.transcript_processing_duration || s.transcriptProcessingDuration)}
                                    </span>
                                  </div>
                                )}
                                {/* Placeholder for future report generation info */}
                                {/* <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Report Generated:</span>
                                  <span className="font-medium text-gray-900">—</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600">Report Link:</span>
                                  <Link to="/reports/..." className="text-blue-600 hover:text-blue-700 hover:underline font-medium">View Report</Link>
                                </div> */}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Queue Status */}
                      {(s.status === 'PROCESSING' || s.status === 'FAILED') && s.id && (
                        <div className="mt-4">
                          <SessionQueueStatus session={s} />
                        </div>
                      )}

                      {s.error_message && s.status !== 'FAILED' && (
                        <div className="mt-4 text-xs text-red-700 bg-red-50 border-l-4 border-red-400 rounded-r-lg p-3">{s.error_message}</div>
                      )}
                    </div>

                    {/* Right side - Action buttons */}
                    <div className="flex flex-col gap-2.5 shrink-0">
                      {s.report_url && (
                        <a 
                          href={s.report_url} 
                          target="_blank" 
                          rel="noreferrer" 
                          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm hover:shadow"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                          <span>Report</span>
                        </a>
                      )}
                      {(s.transcript_status === 'COMPLETED' || s.transcriptStatus === 'COMPLETED') && (
                        <Link 
                          to={`/sessions/${s.id || s.session_id}/edit`} 
                          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg"
                        >
                          <PencilIcon className="h-4 w-4" />
                          <span>Transcript</span>
                        </Link>
                      )}
                      <Link 
                        to={`/sessions/${s.id || s.session_id}`} 
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm hover:shadow"
                      >
                        <EyeIcon className="h-4 w-4" />
                        <span>Details</span>
                      </Link>
                      <button 
                        onClick={() => handleDeleteSession(s.id || s.session_id, s.title)} 
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-red-300 bg-white text-red-700 hover:bg-red-50 hover:border-red-400 transition-all shadow-sm hover:shadow"
                        title="Delete session"
                      >
                        <TrashIcon className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">{totalCount} total</div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-600">
              Per page:
              <select value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }} className="ml-2 px-2 py-1 border border-gray-300 rounded-md">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className={`inline-flex items-center px-3 py-1.5 rounded-md border ${page <= 1 ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <div className="text-sm text-gray-700">Page {page}</div>
            <button onClick={() => setPage(p => p + 1)} className="inline-flex items-center px-3 py-1.5 rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50">
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {notif && (
        <Notification
          type={notif.type}
          title={notif.title}
          message={notif.message}
          onClose={() => setNotif(null)}
          duration={5000}
        />
      )}

      {/* Save Filter Preset Modal */}
      <Modal
        isOpen={savePresetModalOpen}
        onClose={() => {
          setSavePresetModalOpen(false);
          setPresetName('');
        }}
        title="Save Filter Preset"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="preset-name" className="block text-sm font-medium text-gray-700 mb-2">
              Preset Name
            </label>
            <input
              id="preset-name"
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g., My Active Sessions"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveCurrentFilters();
                }
              }}
              autoFocus
            />
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
            <p className="font-medium mb-1">Current filters:</p>
            <ul className="list-disc list-inside space-y-1">
              {debouncedSearch && <li>Search: "{debouncedSearch}"</li>}
              {selectedEvents.length > 0 && <li>Events: {selectedEvents.length} selected</li>}
              {selectedUser && <li>User: {selectedUser.name}</li>}
              {status !== 'all' && <li>Status: {status}</li>}
              {model !== 'all' && <li>Model: {model}</li>}
              {(dateFrom || dateTo) && <li>Date range: {dateFrom || '...'} to {dateTo || '...'}</li>}
              {sort !== 'date_desc' && <li>Sort: {sort}</li>}
            </ul>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => {
                setSavePresetModalOpen(false);
                setPresetName('');
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={saveCurrentFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Preset
            </button>
          </div>
        </div>
      </Modal>

      {/* Retry Transcript Modal */}
      <Modal
        isOpen={retryModalOpen}
        onClose={() => setRetryModalOpen(false)}
        title="Retry Transcript Generation"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Select a provider to retry transcript generation for <strong>"{selectedSessionForRetry?.title || 'this session'}"</strong>
          </p>

          <div>
            <label htmlFor="provider-select" className="block text-sm font-medium text-gray-700 mb-2">
              Transcript Provider
            </label>
            <select
              id="provider-select"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TRANSCRIPT_PROVIDERS.map((provider) => (
                <option key={provider.value} value={provider.value}>
                  {provider.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {TRANSCRIPT_PROVIDERS.find(p => p.value === selectedProvider)?.description}
            </p>
          </div>

          {selectedSessionForRetry?.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                <strong>Previous error:</strong> {selectedSessionForRetry.error_message}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setRetryModalOpen(false)}
              disabled={retryingSession}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleRetryTranscript}
              disabled={retryingSession}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {retryingSession ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="h-4 w-4" />
                  Retry Generation
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}


